// App pages: the cost estimator. Sliders drive a per-primitive monthly cost from pricing.json,
// using the same estimate() the build uses for each app's "cost to own".
import { estimate, money, quantity } from "../content/estimate.ts"
import type { EstimateConfig, Pricing } from "../content/schema.ts"

/** The estimator config the build embeds in each app page: the app's sliders and meters, plus its plan flag. */
type EmbeddedConfig = EstimateConfig & { readonly paid?: boolean }

function mount(box: HTMLElement, pricing: Pricing): void {
  const cfgText = box.querySelector(".est-cfg")?.textContent
  if (!cfgText) return
  const cfg = JSON.parse(cfgText) as EmbeddedConfig
  const inputs = [...box.querySelectorAll<HTMLInputElement>("input[type=range]")]
  const rows = [...box.querySelectorAll<HTMLElement>(".erow[data-p]")]
  // The build renders one row per primitive with its icon and family class; re-renders reuse them.
  const icons = Object.fromEntries(rows.map((r) => [r.dataset.p, r.querySelector(".px-ic")?.outerHTML ?? ""]))
  const fams = Object.fromEntries(
    rows.map((r) => [r.dataset.p, [...r.classList].find((c) => c.startsWith("fam-"))]),
  )
  const total = box.querySelector(".et")
  const plan = box.querySelector(".plan")
  const breakdown = box.querySelector(".ebreak")
  const update = () => {
    const values = Object.fromEntries(inputs.map((i) => [i.dataset.id, Number(i.value)]))
    for (const i of inputs) {
      const out = box.querySelector(`#o-${i.dataset.id}`)
      if (out) out.textContent = quantity(Number(i.value))
    }
    const r = estimate(pricing, cfg, values, { paidRequired: cfg.paid === true })
    if (total) total.textContent = money(r.total)
    if (plan) {
      plan.className = `plan ${r.needsPaid ? "paid" : "free"}`
      plan.textContent = r.needsPaid ? "Needs Workers Paid ($5/mo)" : "Fits the free plan"
    }
    if (breakdown) {
      breakdown.innerHTML =
        (r.needsPaid
          ? `<div class="erow base"><span></span><span class="en">Workers Paid plan</span><span class="eu">monthly base</span><span class="ec">${money(pricing.plans.paidBase)}</span></div>`
          : "") +
        r.rows
          .map(
            (row) =>
              `<div class="erow ${fams[row.id] || ""}" data-p="${row.id}">${icons[row.id] || ""}<span class="en">${row.name}</span><span class="eu">${row.meters.map((m) => `${quantity(m.usage)} ${m.unit}`).join(" · ")}</span><span class="ec">${money(row.cost)}</span></div>`,
          )
          .join("")
    }
    box.dataset.total = r.total.toFixed(6)
  }
  for (const i of inputs) i.addEventListener("input", update)
  update()
}

export function initEstimator(): void {
  const boxes = [...document.querySelectorAll<HTMLElement>(".est")]
  if (!boxes.length) return
  void fetch("/assets/data/pricing.json")
    .then((r) => r.json() as Promise<Pricing>)
    .then((pricing) => {
      for (const box of boxes) mount(box, pricing)
    })
}
